import * as R from 'remeda'
import { FetchedInventory } from '~/features/hooks/useGetInventory'

import { Warning } from '~/features/models/models-list/types'
import { ShopifyProduct } from '~/features/shopify/schemas'
import { ModelV2 } from '~/supabase/types'

const checkForInconsistentSizes = (params: CheckForIssuesParams): CheckForIssuesParams => {
  const { model, shopifyProductMap, warnings, inventory } = params

  if (!model.shopify_product_id) {
    return params
  }

  const shopifyProduct = shopifyProductMap?.[model.stock_code]

  if (!shopifyProduct) {
    return params
  }

  const variantItems = shopifyProduct.variants.nodes
  const variantSizeMap = R.mapToObj(variantItems, (v) => [v.title, true])

  const sizesMissingInShopify = R.unique(
    inventory.filter((inv) => !variantSizeMap[inv.size.toString()]).map((inv) => inv.size)
  )

  return {
    ...params,
    warnings: (warnings ?? []).concat(
      sizesMissingInShopify.length > 0
        ? {
            key: 'missing_shopify_size',
            message: `Some sizes are missing in shopify -> ${sizesMissingInShopify.join(', ')}`,
          }
        : []
    ),
  }
}
const checkForInconsistentShopifyId = (params: CheckForIssuesParams): CheckForIssuesParams => {
  const { model, shopifyProductMap, warnings, inventory } = params
  let updatedWarnings = warnings ?? []

  const shopifyProduct = shopifyProductMap?.[model.stock_code]

  // Check product ID inconsistencies
  if (!shopifyProduct && model.shopify_product_id) {
    updatedWarnings = updatedWarnings.concat({
      key: 'inconsistent_shopify_product_id',
      message: 'The product id exists in the database but not in shopify',
    })
  }
  if (!model.shopify_product_id && shopifyProduct) {
    updatedWarnings = updatedWarnings.concat({
      key: 'inconsistent_shopify_product_id',
      message: 'The product id exists in shopify but not in the database',
    })
  }
  if (
    model.shopify_product_id &&
    shopifyProduct &&
    model.shopify_product_id !== shopifyProduct.id
  ) {
    updatedWarnings = updatedWarnings.concat({
      key: 'inconsistent_shopify_product_id',
      message: 'The product id in the database is inconsistent with the product id in shopify',
    })
  }

  // Check variant and inventory item ID inconsistencies
  if (shopifyProduct) {
    const shopifyVariantMap = R.mapToObj(shopifyProduct.variants.nodes, (v) => [v.title, v])

    // Check for inconsistent product_variant_id and inventory_item_id in inventory entries
    const inventoryWithInconsistentVariantId = inventory.filter((inv) => {
      const shopifyVariant = shopifyVariantMap[inv.size.toString()]
      return (
        inv.product_variant_id && shopifyVariant && inv.product_variant_id !== shopifyVariant.id
      )
    })

    const inventoryWithInconsistentInventoryItemId = inventory.filter((inv) => {
      const shopifyVariant = shopifyVariantMap[inv.size.toString()]
      return (
        inv.inventory_item_id &&
        shopifyVariant &&
        inv.inventory_item_id !== shopifyVariant.inventoryItem.id
      )
    })

    if (inventoryWithInconsistentVariantId.length > 0) {
      const inventoryIds = R.unique(inventoryWithInconsistentVariantId.map((inv) => inv.id))
      updatedWarnings = updatedWarnings.concat({
        key: 'inconsistent_product_variant_id',
        message: `Some inventory entries have inconsistent product_variant_id -> Inventory IDs: ${inventoryIds.join(', ')}`,
      })
    }

    if (inventoryWithInconsistentInventoryItemId.length > 0) {
      const inventoryIds = R.unique(inventoryWithInconsistentInventoryItemId.map((inv) => inv.id))
      updatedWarnings = updatedWarnings.concat({
        key: 'inconsistent_inventory_item_id',
        message: `Some inventory entries have inconsistent inventory_item_id -> Inventory IDs: ${inventoryIds.join(', ')}`,
      })
    }
  }

  return {
    ...params,
    warnings: updatedWarnings,
  }
}

const checkForInconsistentQuantity = (params: CheckForIssuesParams): CheckForIssuesParams => {
  const { model, shopifyProductMap, warnings, inventory } = params

  if (!model.shopify_product_id) {
    return params
  }

  const shopifyProduct = shopifyProductMap?.[model.stock_code]

  if (!shopifyProduct) {
    return params
  }

  const inconsistentQuantities = shopifyProduct.variants.nodes.filter(
    (node) =>
      node.inventoryQuantity !==
      inventory.filter(
        (inv) => inv.status === 'available' && inv.inventory_item_id === node.inventoryItem.id
      ).length
  )

  if (inconsistentQuantities.length > 0) {
    return {
      ...params,
      warnings: (warnings ?? []).concat({
        key: 'inconsistent_shopify_quantity',
        message: `Some variants in shopify has inconsistent quantities -> ${inconsistentQuantities.map((q) => `Size: ${q.title} - Quantity: ${q.inventoryQuantity}`).join(', ')}`,
      }),
    }
  }

  return params
}

const checkForInconsistentVariantPrices = (params: CheckForIssuesParams): CheckForIssuesParams => {
  const { model, shopifyProductMap, warnings, inventory } = params

  const shopifyProduct = shopifyProductMap?.[model.stock_code]

  if (!shopifyProduct) {
    return params
  }

  const { nodes: variantItems } = shopifyProduct.variants

  if (variantItems.length === 0) {
    return {
      ...params,
      warnings: (warnings ?? []).concat({
        key: 'missing_shopify_variants',
        message: 'The product has no variants in shopify',
      }),
    }
  }
  const variantsWithInconsistentPrices = variantItems.filter((v) => v.price !== model.price)

  if (variantsWithInconsistentPrices.length > 0) {
    return {
      ...params,
      warnings: (warnings ?? []).concat({
        key: 'inconsistent_shopify_price',
        message: `Some variants in shopify has inconsistent prices -> ${variantsWithInconsistentPrices.map((p) => p.id).join(', ')}`,
      }),
    }
  }

  const variantsWithInconsistentIds = inventory.filter(
    (v) =>
      variantItems.find((inv) => inv.id === v.product_variant_id) === undefined ||
      variantItems.find((inv) => inv.inventoryItem.id === v.inventory_item_id) === undefined
  )

  if (variantsWithInconsistentIds.length > 0) {
    return {
      ...params,
      warnings: (warnings ?? []).concat({
        key: 'inconsistent_shopify_variant_id',
        message: `Some variants in shopify has inconsistent ids -> ${variantsWithInconsistentIds.map((v) => v.id).join(', ')}`,
      }),
    }
  }
  return params
}

const checkForMissingImages = (params: CheckForIssuesParams): CheckForIssuesParams => {
  const { warnings, images } = params

  if (!images || images.length === 0) {
    return {
      ...params,
      warnings: (warnings ?? []).concat({
        key: 'missing_images',
        message: 'The model has no images',
      }),
    }
  }

  return params
}

type CheckForIssuesParams = {
  model: ModelV2
  shopifyProductMap: Record<string, ShopifyProduct>
  warnings?: Warning[]
  inventory: FetchedInventory[]
  images?: { supplierCode: string; material: string; fileName: string }[]
}

export const checkForIssues = (params: CheckForIssuesParams): Warning[] => {
  const results = R.pipe(
    params,
    checkForInconsistentShopifyId,
    checkForInconsistentQuantity,
    checkForInconsistentVariantPrices,
    checkForInconsistentSizes,
    checkForMissingImages
  )

  return results.warnings ?? []
}
